// The funcionalities in this file is exposed in the automation's config modal, but they are NOT an automation.
import React from "react";
import { useRelayEnvironment } from "react-relay";

import { graphql } from "relay-runtime";
import RelayModernEnvironment from "relay-runtime/lib/store/RelayModernEnvironment";

import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { LoadingSpinner } from "~/ui";
import NameList from "~/ui/avatar/NameList";
import { ButtonLink, HelpCenterLink, Switch } from "~/ui/legacyFellowIndex";
import Selector, { type SelectorOption } from "~/ui/selectors/Selector";

import { Header } from "./Automations/Tasks";

import type { AutoShareRecapSettingsMutation } from "~/__generated__/AutoShareRecapSettingsMutation.graphql";
import type { AutoShareRecapSettingsQuery } from "~/__generated__/AutoShareRecapSettingsQuery.graphql";

import automationStyles from "./Automations/Automations.module.css";

const query = graphql`
	query AutoShareRecapSettingsQuery($streamId: ID!) {
		viewer {
			id
			user {
				id
				fullName
				firstName
			}
			account {
				allowPublicRecap
				emailDomainList
			}
		}
		noteStream(id: $streamId) {
			id
			meetingRecapAutoShareSourceIsFromOrganizer
			meetingRecapAutoShareSource {
				__typename
				... on StreamMeetingRecapAutoShareSettings {
					id
					sendTo_S: sendTo
					setBy {
						id
						fullName
						firstName
					}
				}
				... on UserMeetingRecapAutoShareSettings {
					id
					sendTo_U: sendTo
					user {
						id
						fullName
						firstName
					}
				}
			}
		}
	}
`;

const mutation = graphql`
	mutation AutoShareRecapSettingsMutation($streamId: ID!, $sendTo: StreamMeetingRecapSendTo!, $delete: Boolean!) {
		updateStreamMeetingRecapAutoShareSettings(sendTo: $sendTo, streamId: $streamId) @skip(if: $delete) {
			settings {
				id
				sendTo_S: sendTo
				setBy {
					id
					fullName
					firstName
				}
			}
			stream {
				id
				meetingRecapAutoShareSource {
					__typename
					... on StreamMeetingRecapAutoShareSettings {
						id
						sendTo_S: sendTo
						setBy {
							id
							fullName
							firstName
						}
					}
					... on UserMeetingRecapAutoShareSettings {
						id
						sendTo_U: sendTo
						user {
							id
							fullName
							firstName
						}
					}
				}
			}
		}
		deleteStreamMeetingRecapAutoShareSettings(streamId: $streamId) @include(if: $delete) {
			stream {
				id
				meetingRecapAutoShareSource {
					__typename
					... on StreamMeetingRecapAutoShareSettings {
						id
						sendTo_S: sendTo
						setBy {
							id
							fullName
							firstName
						}
					}
					... on UserMeetingRecapAutoShareSettings {
						id
						sendTo_U: sendTo
						user {
							id
							fullName
							firstName
						}
					}
				}
			}
		}
	}
`;

type Source =
	| NonNullable<AutoShareRecapSettingsQuery["response"]["noteStream"]>["meetingRecapAutoShareSource"]
	| undefined;

function deleteStreamMeetingRecapAutoShareSettings(streamId: string, relayEnvironment: RelayModernEnvironment) {
	return commitMutationPromise<AutoShareRecapSettingsMutation>(relayEnvironment, {
		mutation,
		variables: {
			streamId,
			delete: true,
			sendTo: "NONE",
		},
	});
}

function useEnabledBy(source: Source): [_User | null, _User | null] {
	const [enabledByUser, enabledByOrganizer]: [_User | null, _User | null] = React.useMemo(() => {
		if (!source) return [null, null];
		if (source.__typename === "StreamMeetingRecapAutoShareSettings") {
			return [source.setBy, null];
		} else if (source.__typename === "UserMeetingRecapAutoShareSettings" && source.sendTo_U !== "NONE") {
			return [null, source.user];
		} else {
			return [null, null];
		}
	}, [source]);
	return [enabledByUser, enabledByOrganizer];
}

export function useMeetingRecapAutoShareEnabled(source: Source): boolean {
	const enabled = React.useMemo(() => {
		if (
			(source?.__typename === "StreamMeetingRecapAutoShareSettings" && source.sendTo_S == "NONE") ||
			(source?.__typename === "UserMeetingRecapAutoShareSettings" && source.sendTo_U == "NONE")
		) {
			return false;
		}
		return true;
	}, [source]);
	return enabled;
}

function EnabledByNoticeContent({
	source,
	sourceIsFromOrganizer,
	self,
	streamId,
}: {
	source: Source;
	sourceIsFromOrganizer: boolean | null | undefined;
	self: _User;
	streamId: string;
}) {
	const [enabledByUser, enabledByOrganizer] = useEnabledBy(source);
	const relayEnv = useRelayEnvironment();
	const onDeleteStreamSettings = React.useCallback(() => {
		deleteStreamMeetingRecapAutoShareSettings(streamId, relayEnv);
	}, [streamId, relayEnv]);
	return (
		<>
			{enabledByOrganizer ? (
				sourceIsFromOrganizer ? (
					<p>
						Turned on by the settings of the organizer,{" "}
						<NameList users={[enabledByOrganizer]} selfId={self.id} reflexive />.{" "}
						<HelpCenterLink articleId="5650836">Learn more</HelpCenterLink>.
					</p>
				) : (
					<p>
						Turned on by the settings of <NameList users={[enabledByOrganizer]} reflexive selfId={self.id} />.
					</p>
				)
			) : (
				<></>
			)}
			{enabledByUser && (
				<p>
					Modified by <NameList users={[enabledByUser]} selfId={self.id} />.{" "}
					<ButtonLink onClick={onDeleteStreamSettings}>Reset to use default user settings.</ButtonLink>
				</p>
			)}
		</>
	);
}

type _User = {
	id: string;
	fullName: string;
	firstName: string;
};

type SettingsProps = {
	streamId: string;
	goToConfig: () => void;
};
export function AutoShareRecapSettings({ streamId, goToConfig }: SettingsProps) {
	const data = useLazyLoadQuery<AutoShareRecapSettingsQuery>(
		query,
		{
			streamId,
		},
		{ fetchPolicy: "store-and-network" },
	);
	const source = data.noteStream?.meetingRecapAutoShareSource;
	const sourceIsFromOrganizer = data.noteStream?.meetingRecapAutoShareSourceIsFromOrganizer;
	const enabled = useMeetingRecapAutoShareEnabled(source);
	return (
		<section className={automationStyles.automationBlock}>
			<div className={automationStyles.row}>
				<h1 className={automationStyles.title}>Send Recap</h1>
				<ButtonLink onClick={goToConfig} className={automationStyles.configureLink}>
					Configure
				</ButtonLink>
			</div>
			<div className={automationStyles.row} onClick={undefined} style={{ marginBottom: "0" }}>
				<label className={automationStyles.label}>Automatically email meeting Recaps to invitees.</label>

				<Switch checked={enabled} id={"fake"} tooltipPlacement="top" onClick={goToConfig} />
			</div>
			<div className={automationStyles.row} style={{ font: "var(--paragraph-5)", color: "var(--secondaryTextColor)" }}>
				<EnabledByNoticeContent
					source={source}
					sourceIsFromOrganizer={sourceIsFromOrganizer}
					self={data.viewer.user}
					streamId={streamId}
				/>
			</div>
			{!enabled && (
				<div className={automationStyles.row}>
					<p className={automationStyles.infoBubble}>
						Some users have requested AI Notes for all meetings and will receive them regardless of the settings above.
					</p>
				</div>
			)}
		</section>
	);
}

function useGetStreamLevelSendToWithoutAllOptions(emailDomainList: readonly string[]): readonly SelectorOption[] {
	return [
		{
			value: "INTERNAL",
			label: emailDomainList.length === 1 ? `Only @${emailDomainList[0]} invitees` : "Only invitees from your org",
			"data-test-id": "internal",
		},
		{
			value: "INTERNAL_ON_FELLOW",
			label: "Only Fellow users",
			"data-test-id": "internal-o-fellow",
		},
	];
}

function useGetStreamLevelSendToOptions(emailDomainList: readonly string[]): readonly SelectorOption[] {
	return [
		{
			value: "ALL",
			label: "All meeting invitees",
			"data-test-id": "all",
		},
		...useGetStreamLevelSendToWithoutAllOptions(emailDomainList),
	];
}

function mapUserToStreamSendTo(
	sendTo: "ALL" | "INTERNAL" | "JUST_ME" | "NONE",
): "ALL" | "INTERNAL" | "INTERNAL_ON_FELLOW" | "NONE" {
	if (sendTo === "ALL") {
		return "ALL";
	} else if (sendTo === "INTERNAL") {
		return "INTERNAL";
	} else if (sendTo === "JUST_ME") {
		return "INTERNAL_ON_FELLOW";
	} else if (sendTo === "NONE") {
		return "NONE";
	}
	return "INTERNAL";
}

function mapNoneToInternal(sendTo: "ALL" | "INTERNAL" | "INTERNAL_ON_FELLOW" | "NONE"): Exclude<typeof sendTo, "NONE"> {
	if (sendTo === "NONE") return "INTERNAL";
	return sendTo;
}

function formatEmailDomains(domains: readonly string[]): string {
	if (domains.length === 0) return "";
	if (domains.length === 1) return `@${domains[0]}`;
	if (domains.length === 2) return `@${domains[0]} and @${domains[1]}`;
	return `${domains
		.slice(0, -1)
		.map(domain => `@${domain}`)
		.join(", ")}, and @${domains[domains.length - 1]}`;
}

type ConfigProps = {
	streamId: string;
	onBack: () => void;
};
export function AutoShareRecapSettingsConfig({ streamId, onBack }: ConfigProps) {
	const data = useLazyLoadQuery<AutoShareRecapSettingsQuery>(
		query,
		{
			streamId,
		},
		{ fetchPolicy: "store-and-network" },
	);
	const source = data.noteStream?.meetingRecapAutoShareSource;
	const sourceIsFromOrganizer = data.noteStream?.meetingRecapAutoShareSourceIsFromOrganizer;
	const { allowPublicRecap, emailDomainList } = data.viewer.account;

	const enabled = useMeetingRecapAutoShareEnabled(source);
	const [saving, setSaving] = React.useState(false);

	const prevSendTo = React.useRef(
		source &&
			(source.__typename === "StreamMeetingRecapAutoShareSettings"
				? mapNoneToInternal(source.sendTo_S)
				: source.__typename === "UserMeetingRecapAutoShareSettings"
					? mapUserToStreamSendTo(source.sendTo_U)
					: null),
	);

	const sendTo = React.useMemo(() => {
		if (!source) return prevSendTo.current ?? "INTERNAL";
		if (source.__typename === "StreamMeetingRecapAutoShareSettings") {
			if (source.sendTo_S === "ALL") {
				return "ALL";
			} else if (source.sendTo_S === "INTERNAL") {
				return "INTERNAL";
			} else if (source.sendTo_S === "INTERNAL_ON_FELLOW") {
				return "INTERNAL_ON_FELLOW";
			}
		} else if (source.__typename === "UserMeetingRecapAutoShareSettings") {
			return mapUserToStreamSendTo(source.sendTo_U);
		}
		return prevSendTo.current ?? "INTERNAL";
	}, [source]);

	React.useEffect(() => {
		prevSendTo.current = sendTo;
	}, [sendTo]);

	const relayEnv = useRelayEnvironment();

	const onStreamLevelCheckChange = React.useCallback(
		(v: boolean) => {
			let sendToValue: "ALL" | "INTERNAL" | "INTERNAL_ON_FELLOW" | "NONE" = sendTo;
			if (v === false) {
				sendToValue = "NONE";
			} else if (sendTo === "NONE") {
				sendToValue = "INTERNAL_ON_FELLOW";
			}
			setSaving(true);
			commitMutationPromise<AutoShareRecapSettingsMutation>(relayEnv, {
				mutation,
				variables: {
					streamId,
					sendTo: sendToValue,
					delete: false,
				},
			}).finally(() => {
				setSaving(false);
			});
		},
		[relayEnv, sendTo, streamId],
	);

	const onSendToChange = React.useCallback(
		(option: SelectorOption[][number] | null) => {
			setSaving(true);
			commitMutationPromise<AutoShareRecapSettingsMutation>(relayEnv, {
				mutation,
				variables: {
					streamId,
					sendTo: (option?.value || "INTERNAL") as "ALL" | "INTERNAL" | "INTERNAL_ON_FELLOW" | "NONE",
					delete: false,
				},
			}).finally(() => {
				setSaving(false);
			});
		},
		[relayEnv, streamId],
	);
	const streamLevelSendToOptions = useGetStreamLevelSendToOptions(emailDomainList);
	const streamLevelSendToWithoutAllOptions = useGetStreamLevelSendToWithoutAllOptions(emailDomainList);

	return (
		<>
			<div style={{ display: "flex", justifyContent: "space-between" }}>
				<Header name="Send Recap" onHome={onBack} />
				{saving && <LoadingSpinner size="mini" inline dimmer={false} />}
			</div>
			<section className={automationStyles.automationBlock} style={{ textAlign: "left" }}>
				<div
					style={{
						padding: "1rem",
						borderRadius: "6px",
						backgroundColor: "var(--secondLayerColor)",
						marginBottom: "1rem",
					}}
				>
					<div className={automationStyles.row}>
						<label className={automationStyles.label}>Auto-send Note Taker Recaps:</label>
						<Switch checked={enabled} id={"fake"} tooltipPlacement="top" onCheckedChange={onStreamLevelCheckChange} />
					</div>
					<div style={!enabled ? { opacity: "0.5", userSelect: "none", pointerEvents: "none" } : undefined}>
						<div className={automationStyles.row}>
							<label className={automationStyles.label}>Send recap to:</label>
							<Selector
								options={allowPublicRecap ? streamLevelSendToOptions : streamLevelSendToWithoutAllOptions}
								width="12rem"
								value={streamLevelSendToOptions.find(i => i.value === sendTo)}
								onChange={onSendToChange}
							/>
						</div>
						<div
							className={automationStyles.row}
							style={{ font: "var(--paragraph-5)", color: "var(--secondaryTextColor)" }}
						>
							{sendTo === "INTERNAL_ON_FELLOW"
								? "All meeting attendees with a Fellow account in your workspace will receive the Note Taker Recap."
								: sendTo === "ALL"
									? allowPublicRecap
										? "All meeting attendees will receive the Note Taker Recap."
										: `Public links are disabled by your workspace admin.
									Only attendees with an internal email will receive the Note Taker Recap.`
									: sendTo === "INTERNAL" && emailDomainList.length > 1
										? formatEmailDomains(emailDomainList)
										: ""}
						</div>
					</div>
				</div>
				<div
					className={automationStyles.row}
					style={{ font: "var(--paragraph-5)", color: "var(--secondaryTextColor)" }}
				>
					<EnabledByNoticeContent
						source={source}
						sourceIsFromOrganizer={sourceIsFromOrganizer}
						self={data.viewer.user}
						streamId={streamId}
					/>
				</div>
			</section>
		</>
	);
}
